import { ThemeProvider } from '@mui/material';
import { themeLight } from '@resurs-internal/mui';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { useRouteError } from 'react-router-dom';

import createTranslateService from '../../api/translation';
import { TranslationProvider } from '../../context/TranslationContext';
import { useLocalePath } from '../../hooks/useLocalePath';
import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import { toLanguageCode } from '../../types/LanguageCode';
import ApplicationStyleWrapper from '../ApplicationStyleWrapper/ApplicationStyleWrapper';

/**
 * Router-level `errorElement` for the root route. Renders when an error is thrown in
 * RootPage's own render/effects — e.g. `useSettings`'s effect throwing on an unresolved
 * environment — above/outside where the app's own <ErrorBoundary> sits (it only wraps
 * RootPage's <Outlet /> and siblings). React Router replaces the whole failed route tree
 * with this element, so RootPage's own provider instances (mounted briefly, then torn
 * down with the rest of the tree) can't be reused — this sets up its own independent
 * theme/translation instances instead. Language/country come straight from the URL
 * (useLocalePath), not from getEnvironmentConfig()/settings, since those are exactly what
 * failed to resolve.
 */
const AppCrashFallback = () => {
    const error = useRouteError();
    const { language } = useLocalePath();
    const translate = useMemo(() => createTranslateService(toLanguageCode(language)), [language]);

    useEffect(() => {
        // No-op when Sentry was never initialised (main.tsx only calls Sentry.init() for
        // Environment.Production) — which is exactly the case for an unresolved
        // VITE_ENVIRONMENT, the scenario this fallback exists for. This call only reports
        // *other* errors that reach this fallback during a genuine Production run where
        // Sentry is actually running (e.g. a bug elsewhere in RootPage's tree).
        Sentry.captureException(error, { tags: { source: 'app_crash_fallback' } });
    }, [error]);

    return (
        <ThemeProvider theme={themeLight}>
            <TranslationProvider translate={translate}>
                <ApplicationStyleWrapper>
                    <ErrorPage />
                </ApplicationStyleWrapper>
            </TranslationProvider>
        </ThemeProvider>
    );
};

export default AppCrashFallback;
